import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import customerService from '../services/customerService';
import {
  Box,
  TextField,
  Button,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Grid,
  Typography,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,

} from '@mui/material';
import { Plus, Trash2 } from 'lucide-react';
import Swal from 'sweetalert2';
import { formatToCLP } from '../services/monedaService';
import { NumericFormat } from 'react-number-format';

const RegistroForm = (customer) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    acreedores: '',
    identifier: '',
    firstName: '',
    patrimonio: {
      bienesMuebles: [],
      bienesInmuebles: [],
      derechosHereditarios: {
        madre: false,
        padre: false,
        herencia: false,
        otros: false
      },
      OtroDetalle: ''
    },
    aval: false,
    avalNombre: '',
    instruccionesInmediatas: '',
    domicilios: '',
    expectativa: '',
    jsReferencia: '',
  });

  // Usar useEffect para inicializar los datos del cliente en formData
  useEffect(() => {
    if (customer && customer.customer) {
      setFormData(prevData => ({
        ...prevData,
        identifier: customer.customer.rut || '',  // Asignar el RUT del cliente
        firstName: `${customer.customer.firstName} ${customer.customer.lastName}`, // Asignar el nombre completo del cliente
      }));
    }
  }, [customer]);  // Ejecutar este efecto solo si customer cambia

  // Estados locales para los nuevos bienes muebles e inmuebles
  const [nuevoBienMueble, setNuevoBienMueble] = useState('');
  const [nuevoBienInmueble, setNuevoBienInmueble] = useState('');
  const [nuevoMontoBienMueble, setNuevoMontoBienMueble] = useState('');
  const [nuevoMontoBienInmueble, setNuevoMontoBienInmueble] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleDerechosHereditariosChange = (tipo) => {
    setFormData(prevState => ({
      ...prevState,
      patrimonio: {
        ...prevState.patrimonio,
        derechosHereditarios: {
          ...prevState.patrimonio.derechosHereditarios,
          [tipo]: !prevState.patrimonio.derechosHereditarios[tipo]
        }
      }
    }));
  };

  const handleAgregarBienMueble = () => {
    if (nuevoBienMueble.trim()) {
      setFormData(prevState => ({
        ...prevState,
        patrimonio: {
          ...prevState.patrimonio,
          bienesMuebles: [...prevState.patrimonio.bienesMuebles, { item: nuevoBienMueble.trim(), monto: nuevoMontoBienMueble }]
        }
      }));
      setNuevoBienMueble('');
      setNuevoMontoBienMueble(0);
    }
  };

  const handleAgregarBienInmueble = () => {
    if (nuevoBienInmueble.trim()) {
      setFormData(prevState => ({
        ...prevState,
        patrimonio: {
          ...prevState.patrimonio,
          bienesInmuebles: [...prevState.patrimonio.bienesInmuebles, { item: nuevoBienInmueble.trim(), monto: nuevoMontoBienInmueble }]
        }
      }));
      setNuevoBienInmueble('');
      setNuevoMontoBienInmueble(0);
    }
  };

  const handleEliminarBienMueble = (index) => {
    setFormData(prevState => ({
      ...prevState,
      patrimonio: {
        ...prevState.patrimonio,
        bienesMuebles: prevState.patrimonio.bienesMuebles.filter((_, i) => i !== index)
      }
    }));
  };

  const handleEliminarBienInmueble = (index) => {
    setFormData(prevState => ({
      ...prevState,
      patrimonio: {
        ...prevState.patrimonio,
        bienesInmuebles: prevState.patrimonio.bienesInmuebles.filter((_, i) => i !== index)
      }
    }));
  };

  const handleMontoChange = (values) => {


    // Guardar el número sin formato en el estado
    setNuevoMontoBienMueble(values.value);
  };


  const handleMontoInmuebleChange = (values) => {
    setNuevoMontoBienInmueble(values.value);
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Mostrar los datos del formulario y del cliente en la consola
      // Llamar a la función del servicio para registrar la información
      await customerService.registarInfoComplementaria(formData);

      // Mostrar alerta de éxito y redirigir
      Swal.fire({
        title: "Éxito",
        text: "La información se ha guardado correctamente.",
        icon: "success",
        confirmButtonText: "Aceptar",
      }).then(() => {
        navigate("/LineaNegocio", { state: { customer : customer.customer } });
      });
    } catch (error) {
      // Manejar errores mostrando una alerta y registrando en la consola
      Swal.fire({
        position: "center",
        icon: "error",
        title: "Error en la operación. Intente nuevamente más tarde.",
        showConfirmButton: false,
        timer: 1500,
      });
      console.error("Error en la operación:", error);
    }
  };


  // Funciones para calcular los totales de bienes muebles e inmuebles
  const getTotalBienesMuebles = () => {
    return formData.patrimonio.bienesMuebles.reduce((total, bien) => total + (parseInt(bien.monto) || 0), 0);
  };

  const getTotalBienesInmuebles = () => {
    return formData.patrimonio.bienesInmuebles.reduce((total, bien) => total + (parseInt(bien.monto) || 0), 0);
  };

  const columnsBienesMuebles = [
    {
      name: 'Ítem',
      selector: row => row.item,
      sortable: true,
    },
    {
      name: 'Monto',
      selector: row => formatToCLP(row.monto),
      sortable: true,
      right: true,
    },
    {
      name: 'Acciones',
      cell: (row, index) => (
        <IconButton edge="end" onClick={() => handleEliminarBienMueble(index)}>
          <Trash2 />
        </IconButton>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  const columnsBienesInmuebles = [
    {
      name: 'Ítem',
      selector: row => row.item,
      sortable: true,
    },
    {
      name: 'Monto',
      selector: row => formatToCLP(row.monto),
      sortable: true,
      right: true,
    },
    {
      name: 'Acciones',
      cell: (row, index) => (
        <IconButton edge="end" onClick={() => handleEliminarBienInmueble(index)}>
          <Trash2 />
        </IconButton>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  const NoDataMessage = () => (
    <Typography variant="body2" color="textSecondary">
      No hay registros que mostrar
    </Typography>
  );


  return (
    <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        Registro Complementario
      </Typography>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Identificador"
              name="identifier"
              value={formData.identifier}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Nombre"
              name="firstName"
              value={formData.firstName}
              disabled

            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Situación Patrimonial
            </Typography>



            {/* Bienes Muebles */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" gutterBottom>
                Bienes Muebles
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nuevo Bien Mueble"
                    value={nuevoBienMueble}
                    onChange={(e) => setNuevoBienMueble(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <NumericFormat
                    customInput={TextField}
                    fullWidth
                    label="Valor declarado (CLP)"
                    value={nuevoMontoBienMueble}
                    onValueChange={handleMontoChange}
                    thousandSeparator="."
                    decimalSeparator=","
                    prefix="$"
                    allowNegative={false}
                    decimalScale={0}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleAgregarBienMueble}
                    startIcon={<Plus />}
                  >
                    Agregar
                  </Button>
                </Grid>
              </Grid>
              <DataTable
                columns={columnsBienesMuebles}
                data={formData.patrimonio.bienesMuebles}
                pagination
                highlightOnHover
                pointerOnHover
                noDataComponent={<NoDataMessage />}
              />
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Typography variant="subtitle1">
                  Total Bienes Muebles: {formatToCLP(getTotalBienesMuebles())}
                </Typography>
              </Box>
            </Box>

            {/* Bienes Inmuebles */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" gutterBottom>
                Bienes Inmuebles
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nuevo Bien Inmueble"
                    value={nuevoBienInmueble}
                    onChange={(e) => setNuevoBienInmueble(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <NumericFormat
                    customInput={TextField}
                    fullWidth
                    label="Valor declarado (CLP)"
                    value={nuevoMontoBienInmueble}
                    onValueChange={handleMontoInmuebleChange}
                    thousandSeparator="."
                    decimalSeparator=","
                    prefix="$"
                    allowNegative={false}
                    decimalScale={0}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleAgregarBienInmueble}
                    startIcon={<Plus />}
                  >
                    Agregar
                  </Button>
                </Grid>
              </Grid>
              <DataTable
                columns={columnsBienesInmuebles}
                data={formData.patrimonio.bienesInmuebles}
                pagination
                highlightOnHover
                pointerOnHover
                noDataComponent={<NoDataMessage />}
              />
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Typography variant="subtitle1">
                  Total Bienes Inmuebles: {formatToCLP(getTotalBienesInmuebles())}
                </Typography>
              </Box>
            </Box>
            {/* Derechos Hereditarios */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" gutterBottom>
                Derechos Hereditarios
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.patrimonio.derechosHereditarios.madre}
                      onChange={() => handleDerechosHereditariosChange('madre')}
                    />
                  }
                  label="Madre"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.patrimonio.derechosHereditarios.padre}
                      onChange={() => handleDerechosHereditariosChange('padre')}
                    />
                  }
                  label="Padre"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.patrimonio.derechosHereditarios.herencia}
                      onChange={() => handleDerechosHereditariosChange('herencia')}
                    />
                  }
                  label="Herencia"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.patrimonio.derechosHereditarios.otros}
                      onChange={() => handleDerechosHereditariosChange('otros')}
                    />
                  }
                  label="Otros"
                />
              </FormGroup>
              {formData.patrimonio.derechosHereditarios.otros && (
                <TextField
                  fullWidth
                  label="Especifique otros derechos hereditarios"
                  name="patrimonio.OtroDetalle"
                  value={formData.OtroDetalle}
                  onChange={handleChange}
                  sx={{ mt: 2 }}
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.aval}
                  onChange={(e) => handleChange({
                    target: {
                      name: 'aval',
                      type: 'checkbox',
                      checked: e.target.checked,
                    },
                  })}
                />
              }
              label="Posee Aval"
            />
            {formData.aval && (
              <TextField
                fullWidth
                label="Nombre del Aval"
                name="avalNombre"
                value={formData.avalNombre}
                onChange={handleChange}
                sx={{ mt: 2 }}
              />
            )}
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Instrucciones Inmediatas solicitadas por el cliente"
              name="instruccionesInmediatas"
              value={formData.instruccionesInmediatas}
              onChange={handleChange}
              multiline
              rows={4}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Información Relativa a Domicilios"
              name="domicilios"
              value={formData.domicilios}
              onChange={handleChange}
              multiline
              rows={3}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Expectativa / Proyección"
              name="expectativa"
              value={formData.expectativa}
              onChange={handleChange}
              multiline
              rows={3}
            />
          </Grid>



          <Grid item xs={12}>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" type="submit" size="large">
                Guardar y Continuar
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default RegistroForm;